import { isPlatformBrowser } from '@angular/common';
import { APP_INITIALIZER, LOCALE_ID, PLATFORM_ID } from '@angular/core';
import { I18NEXT_SERVICE, ITranslationService } from 'angular-i18next';
import { InitOptions } from 'i18next';
import HttpApi from 'i18next-http-backend';
import { StorageService } from 'src/app/common/storage/storage.service';
import { LanguageDetector } from './i18n.utils';
import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';

export const getConfig = (
  platformId: NonNullable<unknown>,
  sharryFavouriteLanguage?: string,
  appLanguage?: string,
): InitOptions => ({
  load: 'languageOnly',
  ns: ['translations'],
  defaultNS: 'translations',
  debug: false,
  fallbackLng: 'it',
  supportedLngs: ['it', 'en', 'es', 'fr', 'de'],
  detection: {
    order: ['sharryFavouriteLanguage', 'appLanguage', 'navigator'],
    sharryFavouriteLanguage,
    appLanguage,
    isBrowser: isPlatformBrowser(platformId),
    convertDetectedLanguage: (lng: string) => lng.split('-', 2)[0],
  },
  backend: {
    // No cache buster: the CDN serves the file with max-age=300 and an ETag, so CMS changes show up
    // within 5 minutes and unchanged files revalidate with an empty 304.
    loadPath: 'https://media.sharry.land/app/translations/{{lng}}.json',
  },
  interpolation: {
    escapeValue: false,
  },
});

const appInit =
  (i18next: ITranslationService, platformId: NonNullable<unknown>, storageService: StorageService) => async () => {
    const sharryFavouriteLanguage = await storageService.get('sharryFavouriteLanguage');

    let appLanguage: string | undefined;

    if (Capacitor.isNativePlatform()) {
      const appLang = await Device.getLanguageCode();
      appLanguage = ['it', 'en', 'es', 'fr', 'de'].includes(appLang.value) ? appLang.value : undefined;
    }

    const detector = new LanguageDetector(undefined, {
      order: ['sharryFavouriteLanguage', 'appLanguage', 'navigator'],
      isBrowser: isPlatformBrowser(platformId),
      convertDetectedLanguage: (lng: string) => lng.split('-', 2)[0],
      sharryFavouriteLanguage,
      appLanguage,
    });

    // Server translations are loaded by i18nExpress.
    if (isPlatformBrowser(platformId)) i18next.use(HttpApi);
    i18next.use(detector);

    if (!i18next.isInitialized) {
      const browserLang = isPlatformBrowser(platformId) ? navigator.language.split('-')[0] : undefined;

      await i18next.init({
        ...getConfig(platformId, sharryFavouriteLanguage, appLanguage),
        lng: sharryFavouriteLanguage || appLanguage || browserLang,
      });
    }
    return i18next;
  };

const localeIdFactory = (i18next: ITranslationService) => i18next.language;

export const I18N_PROVIDERS = [
  {
    provide: APP_INITIALIZER,
    useFactory: appInit,
    deps: [I18NEXT_SERVICE, PLATFORM_ID, StorageService],
    multi: true,
  },
  {
    provide: LOCALE_ID,
    deps: [I18NEXT_SERVICE],
    useFactory: localeIdFactory,
  },
];
